import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { orderAPI } from '../services/api';
import OrderCard from '../components/OrderCard';

// Spec: Strict 5-column layout with specific background colors
const COLUMNS = [
  { id: 'Pending', label: 'Pending', headerBg: 'bg-gray-200', textColor: 'text-gray-700' },
  { id: 'In Progress', label: 'In Progress', headerBg: 'bg-blue-100', textColor: 'text-blue-700' },
  { id: 'Ready', label: 'Ready', headerBg: 'bg-green-100', textColor: 'text-green-700' },
  { id: 'Delivered', label: 'Delivered', headerBg: 'bg-purple-100', textColor: 'text-purple-700' },
  { id: 'Cancelled', label: 'Cancelled', headerBg: 'bg-gray-200', textColor: 'text-gray-500' },
];

const KanbanBoard = () => {
  const { selectedShop } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetching a high limit to populate the board comprehensively
      const response = await orderAPI.list({ shop: selectedShop, limit: 500 });
      setOrders(response.data.orders || []);
    } catch (err) {
      setError('Failed to load Kanban board data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [selectedShop]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  if (loading && orders.length === 0) {
    return (
      <div className="flex justify-center items-center h-full min-h-[50vh]">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md m-4">
        <p className="text-red-700 font-medium">{error}</p>
      </div>
    );
  }

  // Pre-filter orders into their respective status buckets
  const columnData = COLUMNS.map(col => ({
    ...col,
    items: orders.filter(order => order.status === col.id)
  }));

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
          Board: {selectedShop}
        </h2>
      </div>

      {/* Horizontal scroll container for mobile, flex row for desktop */}
      <div className="flex-1 overflow-x-auto pb-4 hide-scrollbar snap-x snap-mandatory">
        <div className="flex space-x-4 min-w-max h-full items-start px-1">
          
          {columnData.map((column) => (
            <div 
              key={column.id} 
              className="w-80 flex-shrink-0 bg-gray-50 rounded-lg flex flex-col max-h-[80vh] border border-gray-200 shadow-sm snap-center"
            >
              {/* Sticky Column Header */}
              <div className={`p-3 sticky top-0 rounded-t-lg border-b border-gray-200 ${column.headerBg} flex justify-between items-center z-10`}>
                <h3 className={`font-bold ${column.textColor}`}>{column.label}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-black bg-white bg-opacity-60 ${column.textColor}`}>
                  {column.items.length}
                </span>
              </div>
              
              {/* Column Cards Container */}
              <div className="p-3 flex-1 overflow-y-auto space-y-3 custom-scrollbar">
                {column.items.length === 0 ? (
                  <div className="text-center py-6 text-sm font-medium text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                    No orders
                  </div>
                ) : (
                  column.items.map((order) => (
                    <OrderCard 
                      key={order.order_id} 
                      order={order} 
                      onUpdate={fetchOrders} 
                    />
                  ))
                )}
              </div>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default KanbanBoard;